import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Observable, Subject, timer } from "rxjs";
import { Store } from "@ngrx/store";
import * as fromAuth from '../../store/auth';
import * as fromRouting from '../../../core/store/routing';
import * as fromUserProfile from '../../../core/store/user-profile';
import { NgxSpinnerService } from "ngx-spinner";
import { TranslateService } from "@ngx-translate/core";
import { LabelTranslationService } from "@app/modules/core/languages/label-translation.service";
import { CookieService } from "ngx-cookie-service";
import { DEFAULT_APP_CULTURE_CODE } from "@app/modules/core/languages/language.model";
import { debounceTime, takeUntil, tap, skipUntil, withLatestFrom, filter } from "rxjs/operators";

@Component ({
    selector: 'app-horizon-sso',
    templateUrl: './horizon-sso.component.html',
    styleUrls: ['./horizon-sso.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HorizonSsoComponent implements OnInit, OnDestroy {
  public destroy$ = new Subject<void>;
  public errorMessage$: Observable<string>;
  public isWorking$: Observable<boolean>;
  public isCompletingLogin$: Observable<boolean>;
  languages: string[];
  isWorking = false;
  isWorkingTimeout = new Subject<void>();
  selectedCultureCode: string;
  hasError = false;
  
  constructor (
    private store: Store<any>,
    private routerStore: Store<fromRouting.State>,
    private spinnerService: NgxSpinnerService,
    private translateService: TranslateService,
    private labelTranslationService: LabelTranslationService,
    private cookieService: CookieService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromAuth.loadAvailableLanguagesForLogin());

    if (!!!this.selectedCultureCode) {
      let language = window.localStorage && window.localStorage.getItem('language');
      if (!!!language) {
        this.selectedCultureCode = DEFAULT_APP_CULTURE_CODE;
      } else {
        this.selectedCultureCode = language;
      }
    }
      
    this.isWorkingTimeout.pipe(debounceTime(10000), takeUntil(this.destroy$)).subscribe(() => {
      if (this.isWorking) {
        this.store.dispatch(fromAuth.setIsWorking({ isWorking: false }));
      }
    });

    this.store
      .select(fromAuth.selectLanguages)
      .pipe(takeUntil(this.destroy$))
      .subscribe(languages => {
        this.languages = languages;

        if (!!this.languages && this.languages.length) {
          this.languageSelected();
        }
      }
    );
      
    this.errorMessage$ = this.store.select(fromAuth.selectError);
    this.isWorking$ = this.store.select(fromAuth.selectIsWorking);
    this.isCompletingLogin$ = this.store.select(fromAuth.selectIsCompletingLogin);

    this.routerStore
      .select(fromRouting.selectQueryParams)
      .pipe(withLatestFrom(this.store.select(fromAuth.selectIsCompletingLogin)), takeUntil(this.destroy$))
      .subscribe(([params, isCompletingLogin]) => {
        const jwt = params?.jwt;
        const org = params?.organizationId;

        if (!!jwt) {

          this.store.dispatch(fromAuth.setCompletingLogin({ isCompleting: true }));
          this.store.dispatch(fromAuth.setIsWorking({ isWorking: true }));

          this.store.dispatch(fromAuth.processHorizonLoginResult({ jwt, org }));

          return;
        }        

        if (isCompletingLogin) {
          // we need to reset the state of the login page
          this.store.dispatch(fromAuth.resetLogin());
        }
      }
    );

    this.isWorking$
      .pipe(
        tap(isWorking => {
          isWorking ? this.spinnerService.show() : this.spinnerService.hide();
          this.isWorking = isWorking;
          if (this.isWorking) {
            this.isWorkingTimeout.next();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.errorMessage$
      .pipe(
        skipUntil(timer(50)), // Skip any error already in the state when the login page loads
        filter(err => !!err),
        tap(value => {
          //this.loginHidden = false;
          if (value == 'IP_ADDRESS_IS_RESRICTED') {
            this.store.dispatch(
              fromRouting.go({
                routeData: { path: ['login/ip-restricted'] }
              })
            );
          } else {
            this.hasError = true;
            setTimeout(() => this.store.dispatch(fromRouting.go({ routeData: { path: ['/']}})), 7000);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
  }

  languageSelected(): void {
    this.store.dispatch(
      fromUserProfile.changeTranslationCulture({
        cultureCode: this.selectedCultureCode
      })
    );
    window.localStorage.setItem('language', this.selectedCultureCode);
  }

  getLanguageDisplay(lang: string) {
    if (!!lang) {
      return this.translateService.instant(`LANGUAGES.NATIVE.LANGUAGE-${lang}`);
    }
    return '';
  }
}
